import debounce from 'lodash/debounce';
import { promisify } from 'util';

/**
 * A plugin that adds a searchable functionalities to the module.
 *
 * @param {String} indexName - the index name of the algolia index to use.
 * if omitted will use the moduleName from the module instead.
 * @param {Number} [hitsPerPage=10] - max results to fetch and return from algolia.
 *
 * @returns {Function} that will take a module and apply the plugin.
 */

export default function searchable(indexName, hitsPerPage = 10) {
  return function(module) {
    const ucName = module.name.toUpperCase();

    const INIT = `${ucName}_SEARCH_INIT`;
    const DONE = `${ucName}_SEARCH_DONE`;
    const ERROR = `${ucName}_SEARCH_ERROR`;
    const SET_DATA_KEY = `${ucName}_SEARCH_SET_DATA_KEY`;
    const CLEAT_DATA = `${ucName}_SEARCH_CLEAR_DATA`;

    module.addMutationTypes([INIT, DONE, ERROR, SET_DATA_KEY, CLEAT_DATA]);

    const state = {
      searchQuery: null,
      searching: false,
      searchResults: [],
      searchError: null
    };

    const mutations = {
      [INIT](state, query) {
        state.error = null;
        state.searchQuery = query;
        state.searching = true;
      },
      [DONE](state, results) {
        state.searchResults = results;
        state.searching = false;
      },
      [ERROR](state, error) {
        state.searching = false;
        state.error = error;
      },
      [CLEAT_DATA](state) {
        state.searchQuery = null;
        state.searching = false;
        state.searchResults = [];
        state.searchError = null;
      }
    };

    const actions = {
      search: async ({ commit, state, dispatch }, { query, filters }) => {
        if (!query) {
          commit(CLEAT_DATA);
        } else if (state.searchQuery != query) {
          commit(INIT, query);
          await dispatch('searchAlgolia', filters);
        }
      },
      searchAlgolia: debounce(async function({ commit, state }, filters) {
        const searchIndex = this.$searchClient.initIndex(
          indexName || module.moduleName
        );
        try {
          const result = await promisify(searchIndex.search.bind(searchIndex))({
            query: state.searchQuery,
            hitsPerPage,
            ...(filters ? { filters } : {})
          });
          commit(DONE, result.hits.map(hit => ({ id: hit.objectID, ...hit })));
        } catch (error) {
          commit(ERROR, error);
        }
      }, 400)
    };

    return module.extend({ state, mutations, actions });
  };
}
